import { useState, useEffect } from 'react';
import { Card, Select, Button, message, Typography, Space, Tag } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import FormPreview from '../../components/FormBuilder/FormPreview';

const { Title, Text } = Typography;

interface FormSelectorProps {
  event: any;
  onUpdate: () => void;
}

export default function FormSelector({ event, onUpdate }: FormSelectorProps) {
  const [forms, setForms] = useState<any[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(event.formId || null);
  const [loading, setLoading] = useState(false);
  const [previewForm, setPreviewForm] = useState<any | null>(null);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const res = await fetch('/api/v1/form-builder/forms', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      setForms(data.filter((f: any) => !f.isTemplate));
    } catch (error) {
      message.error('Failed to fetch forms');
    }
  };

  const handleSave = async () => {
    if (!selectedFormId) {
      message.error('Please select a form');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`/api/v1/feedback/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: event.name,
          description: event.description,
          slug: event.slug,
          startDate: event.startDate,
          endDate: event.endDate,
          location: event.location,
          eventType: event.eventType,
          isActive: event.isActive,
          emailTemplateId: event.emailTemplateId,
          formId: selectedFormId,
        }),
      });

      if (res.ok) {
        message.success('Form linked to event successfully');
        onUpdate();
      } else {
        message.error('Failed to link form');
      }
    } catch (error) {
      message.error('Failed to link form');
    } finally {
      setLoading(false);
    }
  };

  const selectedForm = forms.find((f) => f.id === selectedFormId);

  return (
    <div style={{ padding: '24px 0' }}>
      <Card>
        <Title level={4}>Event Form</Title>
        <Text type="secondary">
          Select a form that attendees will fill out for this event. Create forms in the Forms section.
        </Text>

        <div style={{ marginTop: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Select Form
              </Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Choose a form"
                value={selectedFormId}
                onChange={setSelectedFormId}
                options={forms.map((form) => ({
                  label: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{form.name}</span>
                      <Space size="small">
                        <Tag color="blue">{form.layout.columns} cols</Tag>
                        <Tag>{form.layout.fields?.length || 0} fields</Tag>
                      </Space>
                    </div>
                  ),
                  value: form.id,
                }))}
              />
            </div>

            {selectedForm && (
              <div>
                <Card size="small" style={{ background: '#fafafa' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>{selectedForm.name}</Text>
                    </div>
                    {selectedForm.description && (
                      <Text type="secondary">{selectedForm.description}</Text>
                    )}
                    <div>
                      <Space>
                        <Tag color="blue">{selectedForm.layout.columns} Column Grid</Tag>
                        <Tag>{selectedForm.layout.fields?.length || 0} Fields</Tag>
                      </Space>
                    </div>
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setPreviewForm(selectedForm)}
                    >
                      Preview Form
                    </Button>
                  </Space>
                </Card>
              </div>
            )}

            <div>
              <Button
                type="primary"
                onClick={handleSave}
                loading={loading}
                disabled={!selectedFormId}
              >
                Save Form Selection
              </Button>
            </div>
          </Space>
        </div>
      </Card>

      <FormPreview
        visible={!!previewForm}
        form={previewForm}
        onClose={() => setPreviewForm(null)}
      />
    </div>
  );
}
